import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { GET, PUT, alert, loading, Select, toFixed, float, format_date } from "./CustomComponent";
import useIsMobile from "./useIsMobile";

ChartJS.register(ArcElement, Tooltip, Legend);

// หน้าวางแผนการเงินส่วนตัว (แท็บในหน้าโปรไฟล์ของฉัน เฉพาะ super_user)
// แผน 1 ใบต่อปี โหลดจาก API ตอนเปิด/เปลี่ยนปี และบันทึกทั้งแผนในครั้งเดียวเมื่อกดปุ่มบันทึก
// หลักการ: ผู้ใช้กรอกรายได้ต่อเดือน + รายการค่าใช้จ่ายพร้อมความถี่ (ต่อเดือน/ต่อปี)
// ระบบแปลงทุกรายการให้เป็นยอดต่อเดือนและต่อปี แล้วสรุปว่าเหลือเงินออมเท่าไหร่

const MONTHS_PER_YEAR = 12;

// โปรเจกต์ที่กำลังดำเนินการ ดึงจากเส้นเดียวกับหน้าไทม์ไลน์โปรเจกต์ (อ่านอย่างเดียว)
// ราคาและงวดชำระเงินมีเฉพาะตอน super_user เรียก ซึ่งตรงกับเงื่อนไขการเห็นแท็บนี้อยู่แล้ว
const API_PROJECTS = "v1/admin/project-timelines";
const API_PLAN = "v1/admin/financial-plans";
const PROJECT_ACTIVE_STATUS = "In Progress";

// แปลงโปรเจกต์จาก API ให้เหลือเฉพาะที่ใช้ในหน้านี้ ถ้าไม่มีงวดแต่มีราคาเต็ม ให้ถือเป็น 1 รายการ "ราคาเต็ม"
const mapProject = (item) => {
  const price = Number(item.total_price) || 0;
  const raw = Array.isArray(item.payment_installments) ? item.payment_installments : [];
  const installments = raw.length
    ? raw.map((inst, index) => ({
        index,
        label: inst.label || `งวดที่ ${index + 1}`,
        percent: Number(inst.percent) || 0,
        amount: inst.amount !== undefined && inst.amount !== null ? Number(inst.amount) || 0 : (price * (Number(inst.percent) || 0)) / 100,
        is_paid: !!inst.is_paid,
        paid_date: inst.paid_date || "",
        note: inst.note || "",
      }))
    : price > 0
    ? [{ index: 0, label: "ราคาเต็ม", percent: 100, amount: price, is_paid: false, paid_date: "" }]
    : [];
  return {
    id: item.uid,
    code: item.project_code || "",
    name: item.name || "-",
    owner: item.owner || "",
    // วันสุดท้ายของไทม์ไลน์โปรเจกต์ (due_date จาก API ถ้าไม่มีใช้ปลายช่วง range_end)
    end_date: item.due_date || item.range_end || null,
    price,
    installments,
  };
};

const PERIODS = [
  { value: "month", label: "ต่อเดือน", short: "เดือน", icon: "fa-calendar" },
  { value: "year", label: "ต่อปี", short: "ปี", icon: "fa-calendar-days" },
];

// หมวดค่าใช้จ่าย พร้อมสีประจำหมวด (ใช้ทั้งในตาราง ชิป และกราฟวงกลม)
const CATEGORIES = [
  { value: "housing", label: "ที่พักอาศัย", icon: "fa-house", color: "#0c76e5" },
  { value: "food", label: "อาหาร", icon: "fa-utensils", color: "#f5a623" },
  { value: "transport", label: "เดินทาง", icon: "fa-car", color: "#11a4bf" },
  { value: "utility", label: "สาธารณูปโภค", icon: "fa-bolt", color: "#7a55c9" },
  { value: "health", label: "สุขภาพ / ประกัน", icon: "fa-heart-pulse", color: "#2fb572" },
  { value: "debt", label: "ผ่อนชำระ / หนี้สิน", icon: "fa-credit-card", color: "#e55353" },
  { value: "lifestyle", label: "ไลฟ์สไตล์ / บันเทิง", icon: "fa-gamepad", color: "#d24a6e" },
  { value: "travel", label: "ท่องเที่ยว", icon: "fa-plane", color: "#1f8fa8" },
  { value: "pet", label: "สัตว์เลี้ยง", icon: "fa-paw", color: "#c2712a" },
  { value: "family", label: "ครอบครัว", icon: "fa-people-roof", color: "#a3781a" },
  { value: "other", label: "อื่น ๆ", icon: "fa-ellipsis", color: "#6c7a89" },
];

const CATEGORY_MAP = CATEGORIES.reduce((acc, item) => ({ ...acc, [item.value]: item }), {});

const num = (value) => Number(value) || 0;

// แปลงยอดจากความถี่ที่กรอก -> ยอดต่อเดือน / ต่อปี
const convertAmount = (amount, frequency) => {
  const value = num(amount);
  if (frequency === "year") return { month: value / MONTHS_PER_YEAR, year: value };
  return { month: value, year: value * MONTHS_PER_YEAR };
};

const money = (value) => `฿ ${toFixed(value)}`;

// ช่องกรอกจำนวนเงิน: เก็บค่าดิบไม่มีลูกน้ำใน state แต่แสดงผลใส่ลูกน้ำหลักพันให้ (คงจุดทศนิยมที่กำลังพิมพ์ไว้)
const stripCommas = (value) => String(value ?? "").replace(/,/g, "");
const withCommas = (value) => {
  const raw = stripCommas(value);
  if (!raw) return "";
  const [whole, decimal] = raw.split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal !== undefined ? `${grouped}.${decimal}` : grouped;
};
// ใช้ใน onChange: คืนค่าดิบถ้าพิมพ์ถูกต้อง ไม่เช่นนั้นคืน null (ไม่อัปเดต state)
const parseMoneyInput = (value) => {
  const raw = stripCommas(value);
  return float(raw) ? raw : null;
};

const emptyForm = () => ({ name: "", category: "food", amount: "", frequency: "month" });

// สรุปค่าที่บันทึกได้ทั้งหมดเป็นสตริงเดียว ใช้เทียบว่ามีการแก้ไขหลังบันทึกล่าสุดหรือไม่ (เรียง key/ลำดับให้คงที่ก่อนเทียบ)
const serializePlan = (v) =>
  JSON.stringify({
    income: num(v.monthlyIncome),
    taxable: num(v.otherTaxableIncome),
    exempt: num(v.otherExemptIncome),
    target: num(v.savingTarget),
    share: num(v.projectSharePercent),
    items: v.items.map((row) => [row.uid || null, row.name, row.category, num(row.amount), row.frequency]),
    picks: Object.keys(v.projectPicks)
      .filter((key) => (v.projectPicks[key] || []).length)
      .sort()
      .map((key) => [key, [...v.projectPicks[key]].sort((a, b) => a - b)]),
  });

export default function FinancialPlanPanel() {
  const [period, setPeriod] = useState("month");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState([currentYear]);
  // uid ของแผนปีนี้ในฐานข้อมูล (null = ยังไม่เคยบันทึก)
  const [planUid, setPlanUid] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  // snapshot ของค่าที่บันทึกล่าสุด ใช้เทียบกับค่าปัจจุบันเพื่อรู้ว่ามีการแก้ไขค้างอยู่
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  // รายได้อื่น ๆ ที่ได้รับเป็นก้อนทั้งปี แยกเป็นส่วนที่ต้องเสียภาษี (โบนัส งานเสริม) และไม่ต้องเสียภาษี (เงินได้ยกเว้น ของขวัญ)
  const [otherTaxableIncome, setOtherTaxableIncome] = useState("");
  const [otherExemptIncome, setOtherExemptIncome] = useState("");

  // โปรเจกต์ที่กำลังดำเนินการ + งวดที่ยังไม่ชำระซึ่งคาดว่าจะได้รับ (picks[projectId] = [index ของงวด])
  // ผู้ใช้ระบุ % ที่ตัวเองได้รับจากยอดเต็มที่เลือก ส่วนนั้นนับเป็นรายได้ที่ไม่เสียภาษี
  const [projects, setProjects] = useState([]);
  const [projectPicks, setProjectPicks] = useState({});
  const [projectSharePercent, setProjectSharePercent] = useState("100");

  // โปรเจกต์ที่กำลังดำเนินการ โหลดครั้งเดียวตอนเปิดแท็บ
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await GET(API_PROJECTS, { page: 0 });
      if (!cancelled && result?.status) {
        setProjects((result.data || []).filter((item) => item.status === PROJECT_ACTIVE_STATUS).map(mapProject));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getPick = (projectId) => projectPicks[projectId] || [];

  // ติ๊กเลือกได้เฉพาะงวดที่ยังไม่ชำระ (งวดที่ชำระแล้วถือว่าได้รับเงินไปแล้ว ไม่ใช่รายได้ที่คาดว่าจะได้รับ)
  const toggleInstallment = (projectId, index) =>
    setProjectPicks((prev) => {
      const selected = prev[projectId] || [];
      return { ...prev, [projectId]: selected.includes(index) ? selected.filter((i) => i !== index) : [...selected, index] };
    });

  // ยอดจากงวดที่เลือก (คิดเป็นรายได้ทั้งปี) share = ส่วนที่ได้รับจริงตาม % ที่กรอก
  const projectIncome = useMemo(() => {
    const total = { amount: 0, count: 0, share: 0 };
    projects.forEach((project) => {
      const selected = projectPicks[project.id] || [];
      project.installments.forEach((inst) => {
        if (inst.is_paid || !selected.includes(inst.index)) return;
        total.amount += inst.amount;
        total.count += 1;
      });
    });
    total.share = (total.amount * num(projectSharePercent)) / 100;
    return total;
  }, [projects, projectPicks, projectSharePercent]);
  // เป้าหมายเงินออม คิดเป็น % ของรายได้ (ค่าเริ่มต้น 20% ตามแนวทาง 50/30/20)
  const [savingTarget, setSavingTarget] = useState("20");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState("");
  // key ของรายการที่กำลังแก้ไขในฟอร์ม (null = โหมดเพิ่มรายการใหม่)
  const [editingKey, setEditingKey] = useState(null);
  // ลากเรียงลำดับในตาราง (เดสก์ท็อป) มือถือใช้ปุ่มเลื่อนขึ้น/ลงแทนเพราะ drag event บนจอสัมผัสไม่เสถียร
  const isMobile = useIsMobile();
  const [dragKey, setDragKey] = useState(null);
  const [overKey, setOverKey] = useState(null);

  // ย้ายรายการจาก key หนึ่งไปวางตำแหน่งของอีก key (ลำดับใหม่จะถูกส่งเป็น sort_order ตอนบันทึกแผน)
  const moveItem = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setItems((prev) => {
      const from = prev.findIndex((item) => item.key === fromKey);
      const to = prev.findIndex((item) => item.key === toKey);
      if (from < 0 || to < 0) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const moveItemBy = (key, offset) => {
    const index = items.findIndex((item) => item.key === key);
    const target = items[index + offset];
    if (index < 0 || !target) return;
    moveItem(key, target.key);
  };

  const handleDragEnd = () => {
    setDragKey(null);
    setOverKey(null);
  };

  // เอาแผนจาก API มาใส่ state ทั้งหมด (ใช้ทั้งตอนโหลดและหลังบันทึกสำเร็จ เพื่อรับ uid ของรายการใหม่)
  const applyPlan = useCallback((plan) => {
    const picks = {};
    (plan?.project_picks || []).forEach((pick) => {
      if (!pick.project_uid) return;
      picks[pick.project_uid] = [...(picks[pick.project_uid] || []), pick.installment_index];
    });
    const next = {
      monthlyIncome: plan?.monthly_income ? String(plan.monthly_income) : "",
      otherTaxableIncome: plan?.other_taxable_income ? String(plan.other_taxable_income) : "",
      otherExemptIncome: plan?.other_exempt_income ? String(plan.other_exempt_income) : "",
      savingTarget: plan ? String(plan.saving_target_percent ?? 20) : "20",
      projectSharePercent: plan ? String(plan.project_share_percent ?? 100) : "100",
      items: (plan?.expenses || []).map((row) => ({ key: row.uid, uid: row.uid, name: row.name, category: row.category, amount: Number(row.amount) || 0, frequency: row.frequency })),
      projectPicks: picks,
    };
    setPlanUid(plan?.uid || null);
    setLastSaved(plan?.updated_at || null);
    setMonthlyIncome(next.monthlyIncome);
    setOtherTaxableIncome(next.otherTaxableIncome);
    setOtherExemptIncome(next.otherExemptIncome);
    setSavingTarget(next.savingTarget);
    setProjectSharePercent(next.projectSharePercent);
    setItems(next.items);
    setProjectPicks(next.projectPicks);
    setSavedSnapshot(serializePlan(next));
  }, []);

  // โหลดแผนของปีที่เลือก + รายการปีที่เคยบันทึกไว้
  useEffect(() => {
    let cancelled = false;
    (async () => {
      loading(true);
      const [planResult, yearResult] = await Promise.all([GET(API_PLAN, { year }), GET(`${API_PLAN}/years`)]);
      if (!cancelled) {
        if (planResult?.status) applyPlan(planResult.data);
        if (yearResult?.status && yearResult.data?.length) setYears(yearResult.data);
      }
      loading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [year, applyPlan]);

  // มีการแก้ไขที่ยังไม่บันทึก = ค่าปัจจุบันต่างจาก snapshot ที่บันทึกล่าสุด
  const dirty = serializePlan({ monthlyIncome, otherTaxableIncome, otherExemptIncome, savingTarget, projectSharePercent, items, projectPicks }) !== savedSnapshot;

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    loading(true);
    const body = {
      plan_year: year,
      monthly_income: num(monthlyIncome),
      other_taxable_income: num(otherTaxableIncome),
      other_exempt_income: num(otherExemptIncome),
      saving_target_percent: num(savingTarget),
      project_share_percent: num(projectSharePercent),
      expenses: items.map((row) => ({ ...(row.uid ? { uid: row.uid } : {}), name: row.name, category: row.category, amount: num(row.amount), frequency: row.frequency })),
      // ส่งเฉพาะงวดที่ยังไม่ชำระของโปรเจกต์ที่ยังกำลังดำเนินการ (backend ตรวจซ้ำอีกชั้นกับข้อมูลโปรเจกต์จริง)
      project_picks: projects.flatMap((project) =>
        (projectPicks[project.id] || [])
          .filter((index) => project.installments.some((inst) => inst.index === index && !inst.is_paid))
          .map((index) => ({ project_uid: project.id, installment_index: index }))
      ),
    };
    const result = await PUT(API_PLAN, body);
    loading(false);
    setSaving(false);
    if (result?.status) {
      applyPlan(result.data);
      if (!years.includes(year)) setYears((prev) => [...prev, year].sort((a, b) => b - a));
      alert("", "success", "บันทึกแผนการเงินแล้ว", `แผนปี ${year} ถูกบันทึกเรียบร้อย`);
    } else {
      alert("", "warning", "บันทึกไม่สำเร็จ", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    }
  };

  const periodMeta = PERIODS.find((item) => item.value === period) || PERIODS[0];

  // รายได้รวม = รายได้ประจำ (กรอกรายเดือน) + รายได้อื่น ๆ (กรอกรายปี) แปลงเป็นทั้งต่อเดือนและต่อปี
  const salary = useMemo(() => convertAmount(monthlyIncome, "month"), [monthlyIncome]);
  const otherIncome = useMemo(
    () => convertAmount(num(otherTaxableIncome) + num(otherExemptIncome) + projectIncome.share, "year"),
    [otherTaxableIncome, otherExemptIncome, projectIncome]
  );
  const yearlyTaxable = salary.year + num(otherTaxableIncome);
  const yearlyExempt = num(otherExemptIncome) + projectIncome.share;
  const income = useMemo(
    () => ({ month: salary.month + otherIncome.month, year: salary.year + otherIncome.year }),
    [salary, otherIncome]
  );

  // รายการค่าใช้จ่ายทั้งหมด พร้อมยอดที่แปลงแล้ว
  const rows = useMemo(
    () => items.map((item) => ({ ...item, converted: convertAmount(item.amount, item.frequency), meta: CATEGORY_MAP[item.category] || CATEGORY_MAP.other })),
    [items]
  );

  const expense = useMemo(
    () => rows.reduce((acc, row) => ({ month: acc.month + row.converted.month, year: acc.year + row.converted.year }), { month: 0, year: 0 }),
    [rows]
  );

  // ยอดรวมรายหมวด (ตามช่วงเวลาที่เลือก) เรียงจากมากไปน้อย ใช้ทั้งกราฟวงกลมและรายการด้านข้าง
  const byCategory = useMemo(() => {
    const map = {};
    rows.forEach((row) => {
      map[row.category] = (map[row.category] || 0) + row.converted[period];
    });
    return Object.entries(map)
      .map(([category, total]) => ({ ...(CATEGORY_MAP[category] || CATEGORY_MAP.other), total }))
      .sort((a, b) => b.total - a.total);
  }, [rows, period]);

  const remaining = income[period] - expense[period];
  const expenseRatio = income[period] > 0 ? (expense[period] / income[period]) * 100 : 0;
  const savingRate = income[period] > 0 ? Math.max(0, (remaining / income[period]) * 100) : 0;
  const savingGoalAmount = (income[period] * num(savingTarget)) / 100;
  const reachGoal = remaining >= savingGoalAmount && income[period] > 0;

  // สถานะสุขภาพการเงินจากสัดส่วนค่าใช้จ่ายต่อรายได้
  const health = useMemo(() => {
    if (income[period] <= 0) return { label: "ยังไม่ได้กรอกรายได้", color: "#6c7a89", background: "rgba(108, 122, 137, 0.14)", icon: "fa-circle-question" };
    if (remaining < 0) return { label: "ใช้เกินรายได้", color: "#b63636", background: "rgba(229, 83, 83, 0.16)", icon: "fa-triangle-exclamation" };
    if (!reachGoal) return { label: "ยังไม่ถึงเป้าออม", color: "#b97007", background: "rgba(245, 166, 35, 0.16)", icon: "fa-circle-exclamation" };
    return { label: "ตามแผน", color: "#188251", background: "rgba(47, 181, 114, 0.14)", icon: "fa-circle-check" };
  }, [income, period, remaining, reachGoal]);

  const kpis = [
    { label: `รายได้${periodMeta.label}`, value: income[period], icon: "fa-wallet", accent: "#0c76e5" },
    { label: `ค่าใช้จ่าย${periodMeta.label}`, value: expense[period], icon: "fa-receipt", accent: "#e55353" },
    { label: `คงเหลือ${periodMeta.label}`, value: remaining, icon: "fa-piggy-bank", accent: remaining < 0 ? "#b63636" : "#2fb572" },
    { label: "อัตราการออม", value: savingRate, icon: "fa-chart-line", accent: "#7a55c9", unit: "%", hint: `เป้าหมาย ${num(savingTarget)}%` },
  ];

  // ฟอร์มเดียวใช้ทั้งเพิ่มและแก้ไข: ถ้ามี editingKey จะเขียนทับรายการเดิม (คง uid ไว้) ไม่งั้นต่อท้ายเป็นรายการใหม่
  const handleSubmitForm = () => {
    if (!form.name.trim()) return setFormError("กรุณากรอกชื่อรายการ");
    if (num(form.amount) <= 0) return setFormError("กรุณากรอกจำนวนเงินมากกว่า 0");
    const data = { name: form.name.trim(), category: form.category, amount: num(form.amount), frequency: form.frequency };
    if (editingKey) {
      setItems((prev) => prev.map((item) => (item.key === editingKey ? { ...item, ...data } : item)));
    } else {
      setItems((prev) => [...prev, { ...data, key: `i${Date.now()}` }]);
    }
    setForm(emptyForm());
    setEditingKey(null);
    setFormError("");
  };

  const handleEdit = (row) => {
    setEditingKey(row.key);
    setForm({ name: row.name, category: row.category, amount: String(row.amount), frequency: row.frequency });
    setFormError("");
    // เลื่อนไปที่ฟอร์มด้านบนให้เห็นว่ากำลังแก้รายการไหน
    document.getElementById("ptn-plan-expense-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleCancelEdit = () => {
    setEditingKey(null);
    setForm(emptyForm());
    setFormError("");
  };

  // ล้างรายการทั้งหมดต้องยืนยันก่อน (มีผลจริงเมื่อกดบันทึกแผน)
  const handleClearAll = async () => {
    const confirmed = await alert("", "warning", "ล้างรายการค่าใช้จ่ายทั้งหมด", `ต้องการลบรายการค่าใช้จ่ายทั้ง ${items.length} รายการใช่หรือไม่? รายการจะหายจากฐานข้อมูลเมื่อกดบันทึกแผน`, "ล้างทั้งหมด", "ยกเลิก");
    if (!confirmed) return;
    setItems([]);
    handleCancelEdit();
  };

  const handleRemove = (key) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
    if (key === editingKey) handleCancelEdit();
  };

  const doughnutData = {
    labels: byCategory.map((item) => item.label),
    datasets: [
      {
        data: byCategory.map((item) => Math.round(item.total)),
        backgroundColor: byCategory.map((item) => item.color),
        borderColor: "#ffffff",
        borderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${money(ctx.parsed)}` } },
    },
  };

  const categoryOptions = CATEGORIES.map((item) => ({ value: item.value, label: item.label }));
  const frequencyOptions = PERIODS.map((item) => ({ value: item.value, label: item.label }));

  return (
    <div className="ptn-plan">
      <style>{`
        .ptn-plan__card {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          background: linear-gradient(180deg, #ffffff 0%, #f5faff 100%);
          border: 1px solid rgba(18, 125, 255, 0.10);
          box-shadow: 0 6px 16px rgba(12, 118, 229, 0.05);
        }
        .ptn-plan__card-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 700;
          color: #12314d;
          margin-bottom: 0.9rem;
        }
        .ptn-plan__card-title i { color: #0c76e5; }

        /* ===== สวิตช์เลือกช่วงเวลา (เดือน / ปี) ===== */
        .ptn-plan__period {
          display: inline-flex;
          padding: 4px;
          border-radius: 999px;
          background: rgba(12, 118, 229, 0.08);
          border: 1px solid rgba(18, 125, 255, 0.14);
        }
        .ptn-plan__period button {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.42rem 1rem;
          border: none;
          border-radius: 999px;
          background: transparent;
          font-size: 0.86rem;
          font-weight: 600;
          color: #35536e;
          transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .ptn-plan__period button.active {
          color: #ffffff;
          background: linear-gradient(135deg, #0b4f84 0%, #0c76e5 100%);
          box-shadow: 0 8px 18px rgba(12, 118, 229, 0.28);
        }

        /* ===== ช่องกรอกรายได้ / เป้าหมาย ===== */
        .ptn-plan__field label {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #8299ab;
          margin-bottom: 0.35rem;
        }
        .ptn-plan__input {
          display: flex;
          align-items: center;
          border-radius: 14px;
          border: 1px solid rgba(18, 125, 255, 0.18);
          background: #ffffff;
          overflow: hidden;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .ptn-plan__input:focus-within {
          border-color: #0c76e5;
          box-shadow: 0 0 0 4px rgba(12, 118, 229, 0.12);
        }
        .ptn-plan__input > span {
          padding: 0 0.85rem;
          font-weight: 700;
          color: #0b4f84;
          background: rgba(12, 118, 229, 0.08);
          align-self: stretch;
          display: inline-flex;
          align-items: center;
        }
        .ptn-plan__input > input {
          flex: 1;
          min-width: 0;
          border: none;
          outline: none;
          padding: 0.6rem 0.85rem;
          font-weight: 700;
          font-size: 1.05rem;
          color: #12314d;
          background: transparent;
        }
        .ptn-plan__input > input::placeholder { color: #b3c3d0; font-weight: 500; }
        .ptn-plan .form-control {
          border-radius: 14px;
          border-color: rgba(18, 125, 255, 0.18);
        }

        /* ===== การ์ดตัวเลขสรุป ===== */
        .ptn-plan__kpi {
          position: relative;
          overflow: hidden;
          display: flex;
          gap: 0.85rem;
          align-items: flex-start;
          padding: 1rem 1.05rem;
          border-radius: 20px;
          background: linear-gradient(180deg, #ffffff 0%, #f5faff 100%);
          border: 1px solid rgba(18, 125, 255, 0.10);
          box-shadow: 0 6px 16px rgba(12, 118, 229, 0.05);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .ptn-plan__kpi::before {
          content: "";
          position: absolute;
          top: 12px;
          bottom: 12px;
          left: 0;
          width: 3px;
          border-radius: 0 3px 3px 0;
          background: var(--accent, #0c76e5);
          opacity: 0.75;
        }
        .ptn-plan__kpi:hover {
          transform: translateY(-3px);
          border-color: color-mix(in srgb, var(--accent, #0c76e5) 32%, transparent);
          box-shadow: 0 16px 30px color-mix(in srgb, var(--accent, #0c76e5) 18%, transparent);
        }
        .ptn-plan__kpi-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 40px;
          height: 40px;
          border-radius: 13px;
          color: var(--accent, #0c76e5);
          background: color-mix(in srgb, var(--accent, #0c76e5) 12%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--accent, #0c76e5) 22%, transparent);
        }
        .ptn-plan__kpi-label {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #8299ab;
          margin-bottom: 0.2rem;
        }
        .ptn-plan__kpi-value {
          font-size: 1.35rem;
          font-weight: 800;
          line-height: 1.2;
          color: var(--accent, #0c76e5);
          word-break: break-word;
        }
        .ptn-plan__kpi-hint { font-size: 0.76rem; color: #7d94a6; margin-top: 0.15rem; }

        /* ===== แถบสัดส่วนค่าใช้จ่ายต่อรายได้ ===== */
        .ptn-plan__ratio {
          height: 10px;
          border-radius: 999px;
          background: rgba(12, 118, 229, 0.10);
          overflow: hidden;
          display: flex;
        }
        .ptn-plan__ratio > span {
          display: block;
          height: 100%;
          transition: width 0.5s ease;
        }
        .ptn-plan__status {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.3rem 0.8rem;
          border-radius: 999px;
          font-size: 0.8rem;
          font-weight: 700;
        }

        /* ===== รายการหมวดข้างกราฟวงกลม ===== */
        .ptn-plan__legend { display: flex; flex-direction: column; gap: 0.45rem; }
        .ptn-plan__legend-row {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.86rem;
          color: #35536e;
        }
        .ptn-plan__legend-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          flex-shrink: 0;
        }
        .ptn-plan__legend-bar {
          flex: 1;
          height: 6px;
          border-radius: 999px;
          background: rgba(12, 118, 229, 0.08);
          overflow: hidden;
        }
        .ptn-plan__legend-bar > span { display: block; height: 100%; border-radius: 999px; }

        /* ===== ชิปหมวดในตาราง ===== */
        .ptn-plan__chip {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.22rem 0.65rem;
          border-radius: 999px;
          font-size: 0.76rem;
          font-weight: 600;
          white-space: nowrap;
          color: var(--accent, #0c76e5);
          background: color-mix(in srgb, var(--accent, #0c76e5) 12%, #ffffff);
          border: 1px solid color-mix(in srgb, var(--accent, #0c76e5) 22%, transparent);
        }
        .ptn-plan__freq {
          display: inline-block;
          padding: 0.15rem 0.55rem;
          border-radius: 8px;
          font-size: 0.74rem;
          font-weight: 600;
          color: #0b4f84;
          background: rgba(12, 118, 229, 0.08);
        }
        .ptn-plan__col-active { background: rgba(12, 118, 229, 0.06); }

        /* ===== การ์ดภาพรวมต่อเดือน/ต่อปี (กดเพื่อสลับช่วงเวลาได้) ===== */
        .ptn-plan__overview {
          width: 100%;
          text-align: left;
          padding: 0.9rem 1rem;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid rgba(18, 125, 255, 0.14);
          transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
        }
        .ptn-plan__overview:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 22px rgba(12, 118, 229, 0.12);
        }
        .ptn-plan__overview.active {
          border-color: #0c76e5;
          box-shadow: 0 0 0 3px rgba(12, 118, 229, 0.14), 0 12px 26px rgba(12, 118, 229, 0.14);
          background: linear-gradient(180deg, #ffffff 0%, #eef6ff 100%);
        }
        .ptn-plan__overview-head {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          font-weight: 700;
          color: #0b4f84;
          margin-bottom: 0.6rem;
        }
        .ptn-plan__overview-head i {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 8px;
          font-size: 0.78rem;
          color: #0c76e5;
          background: rgba(12, 118, 229, 0.10);
        }
        .ptn-plan__overview-row {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 0.5rem;
          font-size: 0.84rem;
          color: #7d94a6;
          margin-bottom: 0.2rem;
        }
        .ptn-plan__overview-row strong { font-size: 0.92rem; white-space: nowrap; }

        /* ===== รายการโปรเจกต์ที่กำลังดำเนินการ + งวดชำระเงิน ===== */
        .ptn-plan__project {
          padding: 0.9rem 1rem;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid rgba(18, 125, 255, 0.14);
          transition: border-color 0.22s ease, box-shadow 0.22s ease;
        }
        .ptn-plan__project.active {
          border-color: #0c76e5;
          box-shadow: 0 0 0 3px rgba(12, 118, 229, 0.12);
          background: linear-gradient(180deg, #ffffff 0%, #f3f9ff 100%);
        }
        .ptn-plan__project-head {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
        }
        .ptn-plan__installments {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 0.6rem;
        }
        .ptn-plan__installment {
          display: flex;
          align-items: center;
          gap: 0.65rem;
          padding: 0.6rem 0.75rem;
          border-radius: 12px;
          border: 1px solid rgba(18, 125, 255, 0.12);
          background: #f8fbff;
          cursor: pointer;
          font-size: 0.86rem;
          color: #35536e;
          transition: border-color 0.2s ease, background 0.2s ease;
        }
        .ptn-plan__installment:hover { border-color: rgba(12, 118, 229, 0.4); }
        .ptn-plan__installment.checked {
          border-color: #0c76e5;
          background: rgba(12, 118, 229, 0.08);
        }
        /* ป้ายวันสิ้นสุดไทม์ไลน์หลังชื่อโปรเจกต์ (สีส้มให้สะดุดตา) */
        .ptn-plan__deadline {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.2rem 0.7rem;
          border-radius: 999px;
          font-size: 0.78rem;
          font-weight: 700;
          white-space: nowrap;
          color: #b45309;
          background: linear-gradient(135deg, #fff4dc 0%, #ffe6b8 100%);
          border: 1px solid rgba(245, 166, 35, 0.55);
          box-shadow: 0 2px 8px rgba(245, 166, 35, 0.22);
        }
        .ptn-plan__deadline i { color: #f59e0b; }

        /* แถบสรุปยอดที่เลือก + % ที่ได้รับ */
        .ptn-plan__share {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 1rem 2rem;
          padding: 0.9rem 1.1rem;
          border-radius: 16px;
          background: linear-gradient(135deg, #f0fff6 0%, #e8f7ff 100%);
          border: 1px solid rgba(47, 181, 114, 0.28);
        }
        .ptn-plan__share-item { flex: 1 1 180px; min-width: 0; }

        /* งวดที่ชำระแล้ว: แสดงให้เห็นแต่เลือกไม่ได้ */
        .ptn-plan__installment.paid {
          cursor: not-allowed;
          opacity: 0.6;
          background: rgba(47, 181, 114, 0.06);
          border-style: dashed;
        }
        .ptn-plan__installment.paid:hover { border-color: rgba(18, 125, 255, 0.12); }

        /* ===== แถบบันทึกลอยติดขอบล่าง ===== */
        .ptn-plan__savebar {
          position: sticky;
          bottom: 12px;
          z-index: 40;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem 1rem;
          margin-top: 1.25rem;
          padding: 0.8rem 1.1rem;
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(18, 125, 255, 0.16);
          box-shadow: 0 -6px 24px rgba(12, 118, 229, 0.10), 0 12px 30px rgba(12, 118, 229, 0.16);
          transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
        }
        .ptn-plan__savebar--dirty {
          background: rgba(255, 250, 236, 0.94);
          border-color: rgba(245, 166, 35, 0.55);
          box-shadow: 0 -6px 24px rgba(245, 166, 35, 0.14), 0 12px 30px rgba(245, 166, 35, 0.22);
        }
        .ptn-plan__savebar-text {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          font-size: 0.86rem;
          font-weight: 600;
          color: #188251;
        }
        .ptn-plan__savebar--dirty .ptn-plan__savebar-text { color: #b45309; }
        .ptn-plan__savebar-text i { font-size: 1rem; }
        .ptn-plan__savebar .btn { min-width: 150px; }
        @media (max-width: 575px) {
          .ptn-plan__savebar { bottom: 8px; padding: 0.7rem 0.9rem; }
          .ptn-plan__savebar .btn { flex: 1 1 100%; }
        }

        /* แถบหัว (มี Select ปี) ต้องอยู่เหนือการ์ดถัดไป ไม่งั้น dropdown ถูกทับ */
        .ptn-plan__toolbar { position: relative; z-index: 30; }

        /* Select ของโปรเจกต์วาง dropdown แบบ absolute จึงยก z-index ของการ์ดฟอร์มให้ทับตาราง และไม่ตัด overflow */
        .ptn-plan__form { position: relative; z-index: 20; overflow: visible; }
        /* ===== ลากเรียงลำดับรายการค่าใช้จ่าย ===== */
        .ptn-plan__grip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          margin-right: 0.35rem;
          border-radius: 6px;
          color: #a9bccb;
          cursor: grab;
          transition: color 0.2s ease, background 0.2s ease;
        }
        .ptn-plan__grip:hover { color: #0c76e5; background: rgba(12, 118, 229, 0.10); }
        tr.ptn-plan__row-dragging > td { opacity: 0.35; }
        tr.ptn-plan__row-over > td { box-shadow: inset 0 3px 0 #0c76e5; }
        tr.ptn-plan__row-dragging { cursor: grabbing; }

        /* โหมดแก้ไข: ขอบฟอร์มและแถวในตารางเป็นสีส้มให้รู้ว่ากำลังแก้รายการไหน */
        .ptn-plan__form--editing {
          border-color: rgba(245, 166, 35, 0.6);
          box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.16), 0 12px 26px rgba(245, 166, 35, 0.14);
        }
        .ptn-plan__row-editing > td { background: rgba(245, 166, 35, 0.08) !important; }
      `}</style>

      {/* หัวข้อ + เลือกปี + สวิตช์เลือกช่วงเวลา + ปุ่มบันทึก */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-3 ptn-plan__toolbar">
        <div>
          <h5 className="fw-semibold mb-1">วางแผนการเงินส่วนตัว</h5>
          <div className="small text-secondary">กรอกรายได้และค่าใช้จ่าย ระบบจะแปลงเป็นยอดต่อเดือนและต่อปีให้อัตโนมัติ</div>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <div style={{ width: 130 }}>
            <Select
              className="form-control"
              value={String(year)}
              options={years.map((item) => ({ value: String(item), label: `ปี ${item}` }))}
              placeholder="เลือกปี"
              onChange={(value) => value && setYear(Number(value))}
            />
          </div>
          <div className="ptn-plan__period">
            {PERIODS.map((item) => (
              <button key={item.value} type="button" className={period === item.value ? "active" : ""} onClick={() => setPeriod(item.value)}>
                <i className={`fa-solid ${item.icon}`} />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* รายได้ + เป้าหมายเงินออม */}
      <div className="ptn-plan__card p-3 p-md-4 mb-4">
        <div className="ptn-plan__card-title"><i className="fa-solid fa-sack-dollar" />รายได้และเป้าหมาย</div>
        <div className="row g-3 align-items-end">
          <div className="col-12 col-sm-6 col-lg-3 ptn-plan__field">
            <label>รายได้ประจำต่อเดือน</label>
            <div className="ptn-plan__input">
              <span>฿</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={withCommas(monthlyIncome)}
                onChange={(event) => {
                  const raw = parseMoneyInput(event.target.value);
                  if (raw !== null) setMonthlyIncome(raw);
                }}
              />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-lg-3 ptn-plan__field">
            <label>รายได้อื่น ๆ ทั้งปี (เสียภาษี)</label>
            <div className="ptn-plan__input">
              <span>฿</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={withCommas(otherTaxableIncome)}
                onChange={(event) => {
                  const raw = parseMoneyInput(event.target.value);
                  if (raw !== null) setOtherTaxableIncome(raw);
                }}
              />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-lg-3 ptn-plan__field">
            <label>รายได้อื่น ๆ ทั้งปี (ไม่เสียภาษี)</label>
            <div className="ptn-plan__input">
              <span>฿</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={withCommas(otherExemptIncome)}
                onChange={(event) => {
                  const raw = parseMoneyInput(event.target.value);
                  if (raw !== null) setOtherExemptIncome(raw);
                }}
              />
            </div>
          </div>
          <div className="col-12 col-sm-6 col-lg-3 ptn-plan__field">
            <label>เป้าหมายเงินออม</label>
            <div className="ptn-plan__input">
              <input
                type="text"
                inputMode="numeric"
                placeholder="20"
                value={savingTarget}
                onChange={(event) => float(event.target.value, 0) && num(event.target.value) <= 100 && setSavingTarget(event.target.value)}
              />
              <span>%</span>
            </div>
          </div>
        </div>
        <div className="row g-3 mt-1">
          <div className="col-6 col-lg-3">
            <div className="small text-secondary mb-1">รายได้รวม{periodMeta.label}</div>
            <div className="fw-bold" style={{ fontSize: "1.2rem", color: "#0c76e5" }}>{money(income[period])}</div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="small text-secondary mb-1">รายได้ที่เสียภาษีทั้งปี</div>
            <div className="fw-bold" style={{ fontSize: "1.05rem", color: "#b97007" }}>{money(yearlyTaxable)}</div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="small text-secondary mb-1">รายได้ที่ไม่เสียภาษีทั้งปี</div>
            <div className="fw-bold" style={{ fontSize: "1.05rem", color: "#188251" }}>{money(yearlyExempt)}</div>
          </div>
          <div className="col-6 col-lg-3">
            <div className="small text-secondary mb-1">ควรออมได้{periodMeta.label}</div>
            <div className="fw-bold" style={{ fontSize: "1.2rem", color: "#0b4f84" }}>{money(savingGoalAmount)}</div>
          </div>
        </div>
        {otherIncome.year > 0 && (
          <div className="small text-secondary mt-3">
            <i className="fa-solid fa-circle-info me-1" />
            รายได้อื่น ๆ รวม {money(otherIncome.year)} ต่อปี{projectIncome.count > 0 ? ` (รวมส่วนที่ได้รับจากโปรเจกต์ ${num(projectSharePercent)}% = ${money(projectIncome.share)} ไม่เสียภาษี)` : ""} ถูกเฉลี่ยเป็น {money(otherIncome.month)} ต่อเดือน รวมกับรายได้ประจำ {money(salary.month)} ต่อเดือน
          </div>
        )}
      </div>

      {/* โปรเจกต์ที่กำลังดำเนินการ: เลือกงวดที่ยังไม่ชำระซึ่งคาดว่าจะได้รับ นำมารวมเป็นรายได้ (นับเป็นส่วนเสียภาษี) */}
      <div className="ptn-plan__card p-3 p-md-4 mb-4">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
          <div className="ptn-plan__card-title mb-0"><i className="fa-solid fa-diagram-project" />รายได้จากโปรเจกต์ที่กำลังดำเนินการ ({projects.length})</div>
        </div>
        <div className="small text-secondary mb-3">ติ๊กเลือกงวดที่ยังไม่ชำระซึ่งคาดว่าจะได้รับในปีนี้ แล้วระบุ % ที่คุณได้รับจากยอดเต็ม ส่วนนั้นจะรวมเป็นรายได้ที่ไม่เสียภาษีทั้งปี งวดที่ชำระแล้วเลือกไม่ได้</div>

        {/* สรุปยอดที่เลือกทั้งหมด + % ที่ได้รับจากยอดเต็ม */}
        {projectIncome.count > 0 && (
          <div className="ptn-plan__share mb-3">
            <div className="ptn-plan__share-item">
              <div className="ptn-plan__kpi-label">ยอดเต็มที่เลือก</div>
              <div className="fw-bold" style={{ fontSize: "1.1rem", color: "#0b4f84" }}>{money(projectIncome.amount)}</div>
              <div className="small text-secondary">{projectIncome.count} งวด</div>
            </div>
            <div className="ptn-plan__share-item ptn-plan__field">
              <label>% ที่ได้รับจากยอดเต็ม</label>
              <div className="ptn-plan__input" style={{ maxWidth: 160 }}>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="100"
                  value={projectSharePercent}
                  onChange={(event) => float(event.target.value) && num(event.target.value) <= 100 && setProjectSharePercent(event.target.value)}
                />
                <span>%</span>
              </div>
            </div>
            <div className="ptn-plan__share-item">
              <div className="ptn-plan__kpi-label">ส่วนที่ได้รับ (ไม่เสียภาษี)</div>
              <div className="fw-bold" style={{ fontSize: "1.1rem", color: "#188251" }}>{money(projectIncome.share)}</div>
              <div className="small text-secondary">รวมเข้ารายได้ที่ไม่เสียภาษีทั้งปี</div>
            </div>
          </div>
        )}

        {projects.length === 0 ? (
          <div className="text-center text-secondary py-4">ไม่มีโปรเจกต์ที่กำลังดำเนินการ</div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {projects.map((project) => {
              const selected = getPick(project.id);
              const unpaid = project.installments.filter((inst) => !inst.is_paid);
              const selectedCount = unpaid.filter((inst) => selected.includes(inst.index)).length;
              const selectedAmount = unpaid.filter((inst) => selected.includes(inst.index)).reduce((sum, inst) => sum + inst.amount, 0);
              const paidCount = project.installments.length - unpaid.length;
              return (
                <div className={`ptn-plan__project${selectedCount ? " active" : ""}`} key={project.id}>
                  <div className="ptn-plan__project-head">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {project.code && <span className="ptn-plan__freq">{project.code}</span>}
                        <span className="fw-bold text-truncate" style={{ color: "#0b4f84" }}>{project.name}</span>
                        {project.end_date && (
                          <span className="ptn-plan__deadline">
                            <i className="fa-solid fa-flag-checkered" />
                            สิ้นสุด {format_date(project.end_date, "dd/mm/yyyy")}
                          </span>
                        )}
                      </div>
                      <div className="small text-secondary mt-1">
                        ราคา <strong style={{ color: "#12314d" }}>{project.price > 0 ? money(project.price) : "-"}</strong>
                        {project.installments.length > 0 && ` · ชำระแล้ว ${paidCount}/${project.installments.length} งวด`}
                        {project.owner && ` · ${project.owner}`}
                      </div>
                    </div>
                    {unpaid.length > 0 ? (
                      <span className="ptn-plan__status" style={{ color: "#b97007", background: "rgba(245, 166, 35, 0.16)" }}>
                        ยังไม่ชำระ {unpaid.length} งวด {money(unpaid.reduce((sum, inst) => sum + inst.amount, 0))}
                      </span>
                    ) : project.installments.length > 0 ? (
                      <span className="ptn-plan__status" style={{ color: "#188251", background: "rgba(47, 181, 114, 0.14)" }}>ชำระครบแล้ว</span>
                    ) : null}
                  </div>

                  {project.installments.length > 0 ? (
                    <div className="ptn-plan__installments">
                      {project.installments.map((inst) => {
                        const checked = !inst.is_paid && selected.includes(inst.index);
                        return (
                          <label className={`ptn-plan__installment${checked ? " checked" : ""}${inst.is_paid ? " paid" : ""}`} key={inst.index}>
                            <input type="checkbox" className="form-check-input m-0" checked={checked} disabled={inst.is_paid} onChange={() => toggleInstallment(project.id, inst.index)} />
                            <span className="flex-grow-1" style={{ minWidth: 0 }}>
                              <span className="d-block fw-semibold text-truncate">{inst.label} <span className="text-secondary fw-normal">({toFixed(inst.percent, 0)}%)</span></span>
                              <span className="d-block small" style={{ color: inst.is_paid ? "#188251" : "#7d94a6" }}>
                                {inst.is_paid ? `ชำระแล้ว${inst.paid_date ? ` ${inst.paid_date}` : ""}` : "ยังไม่ชำระ"}
                              </span>
                              {inst.note && (
                                <span
                                  className="small text-secondary"
                                  title={inst.note}
                                  style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", wordBreak: "break-word" }}
                                >
                                  <i className="fa-regular fa-note-sticky me-1" />
                                  {inst.note}
                                </span>
                              )}
                            </span>
                            <span className="fw-bold text-nowrap" style={{ color: "#0c76e5" }}>{money(inst.amount)}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="small text-secondary px-1 pt-2">โปรเจกต์นี้ยังไม่ได้ระบุราคาและงวดชำระเงิน</div>
                  )}

                  {selectedCount > 0 && (
                    <div className="d-flex justify-content-between small mt-2 px-1">
                      <span className="text-secondary">คาดว่าจะได้รับ {selectedCount} งวด</span>
                      <span className="fw-bold" style={{ color: "#0b4f84" }}>{money(selectedAmount)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ตัวเลขสรุปตามช่วงเวลาที่เลือก */}
      <div className="row g-3 mb-4">
        {kpis.map((kpi) => (
          <div className="col-6 col-xl-3" key={kpi.label}>
            <div className="ptn-plan__kpi h-100" style={{ "--accent": kpi.accent }}>
              <span className="ptn-plan__kpi-icon"><i className={`fa-solid ${kpi.icon}`} /></span>
              <div style={{ minWidth: 0 }}>
                <div className="ptn-plan__kpi-label">{kpi.label}</div>
                <div className="ptn-plan__kpi-value">{kpi.unit === "%" ? `${toFixed(kpi.value, 1)}%` : money(kpi.value)}</div>
                {kpi.hint && <div className="ptn-plan__kpi-hint">{kpi.hint}</div>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* แถบสัดส่วนค่าใช้จ่ายต่อรายได้ + สถานะ */}
      <div className="ptn-plan__card p-3 p-md-4 mb-4">
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-2">
          <div className="ptn-plan__card-title mb-0"><i className="fa-solid fa-gauge-high" />สัดส่วนค่าใช้จ่ายต่อรายได้{periodMeta.label}</div>
          <span className="ptn-plan__status" style={{ color: health.color, background: health.background }}>
            <i className={`fa-solid ${health.icon}`} />
            {health.label}
          </span>
        </div>
        <div className="ptn-plan__ratio mb-2">
          <span style={{ width: `${Math.min(100, expenseRatio)}%`, background: remaining < 0 ? "linear-gradient(90deg, #e55353 0%, #f5577a 100%)" : "linear-gradient(90deg, #0c76e5 0%, #2fd5e4 100%)" }} />
        </div>
        <div className="d-flex justify-content-between small text-secondary flex-wrap gap-2">
          <span>ใช้ไป {toFixed(expenseRatio, 1)}% ของรายได้</span>
          <span>{remaining < 0 ? `เกินรายได้ ${money(Math.abs(remaining))}` : `เหลือออม ${money(remaining)} (เป้า ${money(savingGoalAmount)})`}</span>
        </div>
      </div>

      {/* กราฟวงกลมรายหมวด + การ์ดเทียบต่อเดือน/ต่อปี */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-xl-5">
          <div className="ptn-plan__card p-3 p-md-4 h-100">
            <div className="ptn-plan__card-title"><i className="fa-solid fa-chart-pie" />ค่าใช้จ่ายรายหมวด{periodMeta.label}</div>
            {byCategory.length > 0 ? (
              <div className="row g-3 align-items-center">
                <div className="col-12 col-sm-5">
                  <div style={{ position: "relative", height: 190 }}>
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                    <div className="position-absolute top-50 start-50 translate-middle text-center" style={{ pointerEvents: "none" }}>
                      <div className="small text-secondary">รวม</div>
                      <div className="fw-bold" style={{ color: "#12314d", fontSize: "0.95rem" }}>{money(expense[period])}</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-7">
                  <div className="ptn-plan__legend">
                    {byCategory.map((item) => {
                      const share = expense[period] > 0 ? (item.total / expense[period]) * 100 : 0;
                      return (
                        <div className="ptn-plan__legend-row" key={item.value}>
                          <span className="ptn-plan__legend-dot" style={{ background: item.color }} />
                          <span style={{ width: 120 }} className="text-truncate">{item.label}</span>
                          <div className="ptn-plan__legend-bar"><span style={{ width: `${share}%`, background: item.color }} /></div>
                          <span className="fw-semibold text-nowrap" style={{ width: 44, textAlign: "right" }}>{toFixed(share, 0)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-secondary py-5">ยังไม่มีรายการค่าใช้จ่าย</div>
            )}
          </div>
        </div>
        <div className="col-12 col-xl-7">
          <div className="ptn-plan__card p-3 p-md-4 h-100">
            <div className="ptn-plan__card-title"><i className="fa-solid fa-table-columns" />ภาพรวมค่าใช้จ่าย ต่อเดือน / ต่อปี</div>
            <div className="row g-3">
              {PERIODS.map((item) => {
                const left = income[item.value] - expense[item.value];
                const ratio = income[item.value] > 0 ? Math.min(100, (expense[item.value] / income[item.value]) * 100) : 0;
                const active = period === item.value;
                return (
                  <div className="col-12 col-md-6" key={item.value}>
                    <button type="button" className={`ptn-plan__overview${active ? " active" : ""}`} onClick={() => setPeriod(item.value)}>
                      <div className="ptn-plan__overview-head">
                        <i className={`fa-solid ${item.icon}`} />
                        {item.label}
                      </div>
                      <div className="ptn-plan__overview-row"><span>รายได้</span><strong style={{ color: "#0c76e5" }}>{money(income[item.value])}</strong></div>
                      <div className="ptn-plan__overview-row"><span>ค่าใช้จ่าย</span><strong style={{ color: "#e55353" }}>{money(expense[item.value])}</strong></div>
                      <div className="ptn-plan__ratio my-2" style={{ height: 6 }}>
                        <span style={{ width: `${ratio}%`, background: left < 0 ? "#e55353" : "linear-gradient(90deg, #0c76e5 0%, #2fd5e4 100%)" }} />
                      </div>
                      <div className="ptn-plan__overview-row"><span>คงเหลือ</span><strong style={{ color: left < 0 ? "#b63636" : "#188251" }}>{money(left)}</strong></div>
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="small text-secondary mt-3">* คิดจาก 1 ปี = {MONTHS_PER_YEAR} เดือน กดที่การ์ดเพื่อสลับช่วงเวลาที่ต้องการดู</div>
          </div>
        </div>
      </div>

      {/* ฟอร์มเพิ่ม/แก้ไขรายการค่าใช้จ่าย */}
      <div id="ptn-plan-expense-form" className={`ptn-plan__card p-3 p-md-4 mb-4 ptn-plan__form${editingKey ? " ptn-plan__form--editing" : ""}`}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="ptn-plan__card-title mb-0">
            <i className={`fa-solid ${editingKey ? "fa-pen-to-square" : "fa-circle-plus"}`} />
            {editingKey ? "แก้ไขรายการค่าใช้จ่าย" : "เพิ่มรายการค่าใช้จ่าย"}
          </div>
          {editingKey && (
            <span className="ptn-plan__status" style={{ color: "#b97007", background: "rgba(245, 166, 35, 0.16)" }}>
              <i className="fa-solid fa-pen" />
              กำลังแก้ไข: {items.find((item) => item.key === editingKey)?.name}
            </span>
          )}
        </div>
        <div className="row g-3 align-items-end">
          <div className="col-12 col-md-4 ptn-plan__field">
            <label>ชื่อรายการ</label>
            <input
              className="form-control"
              placeholder="เช่น ค่าเช่าบ้าน ค่าอาหาร"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              onKeyDown={(event) => event.key === "Enter" && handleSubmitForm()}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-3 ptn-plan__field">
            <label>หมวดหมู่</label>
            <Select className="form-control" value={form.category} options={categoryOptions} placeholder="เลือกหมวด" onChange={(value) => setForm({ ...form, category: value })} />
          </div>
          <div className="col-6 col-sm-3 col-md-2 ptn-plan__field">
            <label>จำนวนเงิน</label>
            <input
              className="form-control text-end"
              inputMode="decimal"
              placeholder="0.00"
              value={withCommas(form.amount)}
              onChange={(event) => {
                const raw = parseMoneyInput(event.target.value);
                if (raw !== null) setForm({ ...form, amount: raw });
              }}
              onKeyDown={(event) => event.key === "Enter" && handleSubmitForm()}
            />
          </div>
          <div className="col-6 col-sm-3 col-md-2 ptn-plan__field">
            <label>ความถี่</label>
            <Select className="form-control" value={form.frequency} options={frequencyOptions} placeholder="ความถี่" onChange={(value) => setForm({ ...form, frequency: value })} />
          </div>
          <div className={`col-12 ${editingKey ? "col-md-12 col-xl-1" : "col-md-1"} d-flex gap-2`}>
            <button
              type="button"
              className={`btn rounded-3 flex-grow-1 ${editingKey ? "btn-warning text-white" : "btn-primary"}`}
              style={{ minHeight: 42 }}
              title={editingKey ? "บันทึกการแก้ไข" : "เพิ่มรายการ"}
              onClick={handleSubmitForm}
            >
              <i className={`fa-solid ${editingKey ? "fa-check" : "fa-plus"}`} />
              <span className={editingKey ? "d-xl-none ms-2" : "d-md-none ms-2"}>{editingKey ? "บันทึกการแก้ไข" : "เพิ่มรายการ"}</span>
            </button>
            {editingKey && (
              <button type="button" className="btn btn-outline-secondary rounded-3" style={{ minHeight: 42 }} title="ยกเลิกการแก้ไข" onClick={handleCancelEdit}>
                <i className="fa-solid fa-xmark" />
                <span className="d-xl-none ms-2">ยกเลิก</span>
              </button>
            )}
          </div>
        </div>
        {formError && <div className="small mt-2" style={{ color: "#b63636" }}><i className="fa-solid fa-circle-exclamation me-1" />{formError}</div>}
      </div>

      {/* ตารางรายการ แสดงยอดทั้งต่อเดือนและต่อปี คอลัมน์ที่ตรงกับช่วงที่เลือกจะถูกเน้น */}
      <div className="ptn-plan__card p-3 p-md-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <div className="ptn-plan__card-title mb-0"><i className="fa-solid fa-list-check" />รายการค่าใช้จ่ายทั้งหมด ({rows.length})</div>
            {rows.length > 1 && (
              <div className="small text-secondary mt-1">
                <i className={`fa-solid ${isMobile ? "fa-arrows-up-down" : "fa-grip-vertical"} me-1`} />
                {isMobile ? "ใช้ปุ่มลูกศรเพื่อเลื่อนลำดับรายการ" : "ลากที่จุดจับหน้าชื่อรายการเพื่อจัดลำดับ"}
              </div>
            )}
          </div>
          {rows.length > 0 && (
            <button type="button" className="btn btn-sm btn-outline-secondary rounded-3" onClick={handleClearAll}>
              <i className="fa-solid fa-broom me-1" />ล้างทั้งหมด
            </button>
          )}
        </div>
        <div className="table-responsive table-mobile-cards ptn-profile-cards">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium">รายการ</th>
                <th className="text-secondary fw-medium text-nowrap">หมวด</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ยอดที่กรอก</th>
                {PERIODS.map((item) => (
                  <th key={item.value} className={`text-secondary fw-medium text-end text-nowrap${period === item.value ? " ptn-plan__col-active" : ""}`}>{item.label}</th>
                ))}
                <th className="text-secondary fw-medium text-center text-nowrap">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((row, rowIndex) => (
                  <tr
                    key={row.key}
                    className={[
                      row.key === editingKey ? "ptn-plan__row-editing" : "",
                      row.key === dragKey ? "ptn-plan__row-dragging" : "",
                      row.key === overKey && row.key !== dragKey ? "ptn-plan__row-over" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    draggable={!isMobile && rows.length > 1}
                    onDragStart={(event) => {
                      setDragKey(row.key);
                      event.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(event) => {
                      if (!dragKey) return;
                      event.preventDefault();
                      if (overKey !== row.key) setOverKey(row.key);
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      moveItem(dragKey, row.key);
                      handleDragEnd();
                    }}
                    onDragEnd={handleDragEnd}
                  >
                    <td data-label="รายการ" className="fw-semibold" style={{ color: "#0b4f84" }}>
                      {!isMobile && rows.length > 1 && (
                        <span className="ptn-plan__grip" title="ลากเพื่อจัดลำดับ">
                          <i className="fa-solid fa-grip-vertical" />
                        </span>
                      )}
                      {row.name}
                      {row.key === editingKey && <span className="ptn-plan__freq ms-2" style={{ color: "#b97007", background: "rgba(245, 166, 35, 0.16)" }}>กำลังแก้ไข</span>}
                    </td>
                    <td data-label="หมวด">
                      <span className="ptn-plan__chip" style={{ "--accent": row.meta.color }}>
                        <i className={`fa-solid ${row.meta.icon}`} />
                        {row.meta.label}
                      </span>
                    </td>
                    <td data-label="ยอดที่กรอก" className="text-end text-nowrap">
                      {money(row.amount)} <span className="ptn-plan__freq ms-1">{PERIODS.find((item) => item.value === row.frequency)?.label}</span>
                    </td>
                    {PERIODS.map((item) => (
                      <td key={item.value} data-label={item.label} className={`text-end text-nowrap${period === item.value ? " ptn-plan__col-active fw-bold" : ""}`}>
                        {money(row.converted[item.value])}
                      </td>
                    ))}
                    <td data-label="จัดการ" className="text-center text-nowrap">
                      {isMobile && rows.length > 1 && (
                        <>
                          <button type="button" className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="เลื่อนขึ้น" disabled={rowIndex === 0} onClick={() => moveItemBy(row.key, -1)}>
                            <i className="fa-solid fa-arrow-up" />
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-secondary rounded-3 me-1" title="เลื่อนลง" disabled={rowIndex === rows.length - 1} onClick={() => moveItemBy(row.key, 1)}>
                            <i className="fa-solid fa-arrow-down" />
                          </button>
                        </>
                      )}
                      <button type="button" className="btn btn-sm btn-outline-primary rounded-3 me-1" title="แก้ไขรายการ" onClick={() => handleEdit(row)}>
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-danger rounded-3" title="ลบรายการ" onClick={() => handleRemove(row.key)}>
                        <i className="fa-solid fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center text-secondary py-5">ยังไม่มีรายการค่าใช้จ่าย เพิ่มรายการแรกจากฟอร์มด้านบน</td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                {/* รายรับแยกตามประเภทภาษี แล้วรวม (ยอดต่อปีของส่วนเสียภาษี = รายได้ประจำ x 12 + รายได้อื่น ๆ ที่เสียภาษี) */}
                <tr className="text-secondary" style={{ background: "rgba(245, 166, 35, 0.05)" }}>
                  <td colSpan="3" className="text-end">รายได้ที่เสียภาษี</td>
                  {PERIODS.map((item) => (
                    <td key={item.value} className={`text-end text-nowrap${period === item.value ? " ptn-plan__col-active" : ""}`} style={{ color: "#b97007" }}>
                      {money(item.value === "year" ? yearlyTaxable : yearlyTaxable / MONTHS_PER_YEAR)}
                    </td>
                  ))}
                  <td />
                </tr>
                <tr className="text-secondary" style={{ background: "rgba(47, 181, 114, 0.04)" }}>
                  <td colSpan="3" className="text-end">รายได้ที่ไม่เสียภาษี</td>
                  {PERIODS.map((item) => (
                    <td key={item.value} className={`text-end text-nowrap${period === item.value ? " ptn-plan__col-active" : ""}`} style={{ color: "#188251" }}>
                      {money(item.value === "year" ? yearlyExempt : yearlyExempt / MONTHS_PER_YEAR)}
                    </td>
                  ))}
                  <td />
                </tr>
                <tr className="fw-bold" style={{ background: "rgba(12, 118, 229, 0.08)" }}>
                  <td colSpan="3" className="text-end">รวมรายรับ</td>
                  {PERIODS.map((item) => (
                    <td key={item.value} className={`text-end text-nowrap${period === item.value ? " ptn-plan__col-active" : ""}`} style={{ color: "#0c76e5" }}>
                      {money(income[item.value])}
                    </td>
                  ))}
                  <td />
                </tr>
                <tr className="fw-bold" style={{ background: "rgba(12, 118, 229, 0.05)" }}>
                  <td colSpan="3" className="text-end">รวมค่าใช้จ่าย</td>
                  {PERIODS.map((item) => (
                    <td key={item.value} className={`text-end text-nowrap${period === item.value ? " ptn-plan__col-active" : ""}`} style={{ color: "#b63636" }}>
                      {money(expense[item.value])}
                    </td>
                  ))}
                  <td />
                </tr>
                <tr className="fw-bold" style={{ background: "rgba(47, 181, 114, 0.06)" }}>
                  <td colSpan="3" className="text-end">คงเหลือหลังหักค่าใช้จ่าย</td>
                  {PERIODS.map((item) => {
                    const left = income[item.value] - expense[item.value];
                    return (
                      <td key={item.value} className={`text-end text-nowrap${period === item.value ? " ptn-plan__col-active" : ""}`} style={{ color: left < 0 ? "#b63636" : "#188251" }}>
                        {money(left)}
                      </td>
                    );
                  })}
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* แถบบันทึกลอยติดขอบล่าง: เห็นสถานะและกดบันทึกได้ตลอดโดยไม่ต้องเลื่อนหน้า */}
      <div className={`ptn-plan__savebar${dirty ? " ptn-plan__savebar--dirty" : ""}`}>
        <div className="ptn-plan__savebar-text">
          <i className={`fa-solid ${dirty ? "fa-pen-to-square" : "fa-circle-check"}`} />
          <span>
            {dirty
              ? "มีการแก้ไขที่ยังไม่ได้บันทึก"
              : planUid
              ? `บันทึกล่าสุด ${lastSaved ? format_date(lastSaved, "dd/mm/yyyy") : "-"}`
              : `ยังไม่มีแผนของปี ${year}`}
          </span>
        </div>
        <button type="button" className={`btn rounded-3 fw-semibold ${dirty ? "btn-warning text-white" : "btn-primary"}`} disabled={saving} onClick={handleSave}>
          <i className="fa-solid fa-floppy-disk me-2" />
          {saving ? "กำลังบันทึก..." : "บันทึกแผน"}
        </button>
      </div>
    </div>
  );
}
